import * as React from "react";
import type { StyledProps } from "styled-components";
import styled, { css } from "styled-components";
import { isFunction } from "lodash";

import type { TooltipPosition } from "../../tooltip/tooltip";
import Tooltip from "../../tooltip/tooltip";
import Icon from "../../decorations/icons/icon";
import type { ExtractSCProps } from "../../../types/ExtractSCProps";

type ButtonHTMLType = "button" | "submit" | "reset";
type ButtonPlacement = "right" | "left";
export type ButtonType =
    | "primary"
    | "secondary"
    | "tertiary"
    | "outline"
    | "secondaryOutline";

export type ButtonSize = "small" | "large" | "medium";

type BtnProps = {
    as?: "button" | "a";
    id?: string;
    placement?: ButtonPlacement;
    className?: string;
    disabled?: boolean;
    htmlType?: ButtonHTMLType;
    tabIndex?: number;
    href?: string;
    style?: React.CSSProperties;
    [`aria-label`]?: string;
    [`data-testid`]?: string;
    form?: string;
    icon?: string | React.ReactElement;
    children?: React.ReactNode;
    onMouseEnter?: React.MouseEventHandler;
    onMouseLeave?: React.MouseEventHandler;
    onClick?: React.EventHandler<any>;
};

class Btn extends React.Component<BtnProps> {
    render() {
        const { id, icon } = this.props;
        const Representation = this.props.as || "button";

        return (
            <Representation
                //@ts-ignore: not sure if placement does anything really ?
                placement={this.props.placement ?? `left`}
                className={this.props.className}
                type={this.props.htmlType}
                disabled={this.props.disabled}
                tabIndex={this.props.tabIndex}
                onClick={this.onClick.bind(this)}
                onMouseEnter={this.props.onMouseEnter}
                onMouseLeave={this.props.onMouseLeave}
                href={this.props.href}
                id={id}
                style={this.props.style}
                aria-label={this.props[`aria-label`]}
                data-testid={this.props[`data-testid`]}
                form={this.props.form}
            >
                {this.props.children ? (
                    <span>{this.props.children}</span>
                ) : null}
                {icon && this.renderIcon()}
            </Representation>
        );
    }

    renderIcon() {
        const { icon } = this.props;
        if (typeof icon === "string") {
            return <i className={`gb-icon-${icon}`} />;
        } else if (React.isValidElement(icon)) {
            return icon;
        }
        return null;
    }

    onClick(event: React.MouseEvent) {
        if (!this.props.disabled && isFunction(this.props.onClick)) {
            this.props.onClick(event);
        }
    }
}

export const TextButton = styled(Btn)<{
    type?: ButtonType;
}>`
    align-self: ${(props) =>
        props.placement === `right` ? `flex-end` : `flex-start`};
    max-width: max-content;
    border: none;
    background: none;
    padding: ${(props) => props.theme.smallPadding} 0;
    color: ${(props) => props.theme.colors.primary};
    text-decoration: underline;
    cursor: pointer;

    i {
        margin-left: ${(props) => props.theme.smallPadding};
    }

    ${(props) => {
        if (props.type === `primary`) {
            return css`
                color: ${props.theme.colors.primary};
            `;
        } else if (props.type === `secondary`) {
            return css`
                color: ${props.theme.colors.secondary};
            `;
        }
    }};

    &:not([disabled]):hover {
        ${(props) => {
            if (props.type === `primary`) {
                return css`
                    color: ${props.theme.colors.primaryDark};
                `;
            } else if (props.type === `secondary`) {
                return css`
                    color: ${props.theme.colors.secondaryDark};
                `;
            }
        }};
    }

    &[disabled] {
        cursor: not-allowed;
        color: ${(props) => props.theme.colors.textDisabled};
    }
`;

const disabledStyles = (props: StyledProps<{ type: ButtonType }>) => {
    if (props.type === `outline`) {
        return css`
            background-color: transparent;
            border-color: ${props.theme.colors.secondaryDark};
            color: ${props.theme.colors.secondaryDark};
            opacity: 0.5;
        `;
    } else {
        return css`
            background-color: ${props.theme.colors.button.disabledBg};
            border: none;
            box-shadow: inset 0px -2px 0px ${props.theme.colors.button.disabledBorder};
            color: ${props.theme.colors.button.disabledText};
        `;
    }
};

export const Button = styled(Btn).attrs<{
    disabled?: boolean;
    htmlType?: ButtonHTMLType;
}>((vals) => {
    return {
        disabled: !!vals.disabled,
        htmlType: vals.htmlType ?? `button`
    };
})<{
    type?: ButtonType;
    size?: ButtonSize;
    iconPosition?: "left" | "right";
    darkTheme?: boolean;
}>`
    width: auto;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;

    text-decoration: none;

    font-size: ${({ theme, size = "medium" }) =>
        `${parseInt(theme.fontSize) * theme[size]}px`};
    line-height: ${(props) => props.theme.verticalRhythm};
    padding: ${(props) => `${props.theme.padding}em`};

    border-radius: ${(props) => props.theme.borderRadius};
    text-transform: uppercase;

    border-style: solid;
    border-width: 0 0 2px 0;

    transition: all 0.3s ease-in-out 0.1s;

    position: relative;

    ${({ darkTheme, theme, type = "primary" }) => {
        if (type === `primary`) {
            return css`
                border-color: ${theme.colors.primaryDark};
                background-color: ${theme.colors.primary};
                color: ${theme.colors.primaryButtonText};
                border-width: 0 0 2px 0;
            `;
        } else if (type === `secondary`) {
            return css`
                border-color: ${theme.colors.secondaryDark};
                background-color: ${theme.colors.secondary};
                color: ${theme.colors.primaryButtonText};
                border-width: 0 0 2px 0;
            `;
        } else if (type === `tertiary`) {
            return css`
                border-color: #596281;
                box-shadow: 0 2px 0 ${theme.colors.secondaryDark};
                background-color: #596281;
                color: ${theme.colors.primaryButtonText};
                border-width: 0 0 1px 0;
            `;
        } else if (type === `outline`) {
            return css`
                border-color: ${darkTheme
                    ? theme.colors.secondaryLighter
                    : theme.colors.secondaryDark};
                background-color: transparent;
                color: ${darkTheme
                    ? theme.colors.secondaryLighter
                    : theme.colors.secondary};
                border-width: 1px;
            `;
        } else if (type === `secondaryOutline`) {
            return css`
                height: 61px;
                width: 100%;
                border: 1px solid ${theme.colors.text7};
                color: ${theme.colors.text7};
                padding: 0.5em;
                background: transparent;
                justify-content: center;
            `;
        }
    }};

    &:not(:first-of-type) {
        margin-left: 0.5em;
    }

    &:hover {
        text-decoration: none;
    }

    &:not([disabled]):hover,
    &:not([disabled]):focus {
        cursor: pointer;
        color: ${(props) => props.theme.colors.hoverColour};
        ${({ theme, type = "primary" }) => {
            if (type === `primary`) {
                return css`
                    color: ${theme.colors.primaryButtonText};
                    background-color: ${theme.colors.primaryDark};
                `;
            } else if (type === `secondary`) {
                return css`
                    background-color: ${theme.colors.secondaryDark};
                `;
            } else if (type === `secondaryOutline`) {
                return css`
                    color: ${theme.colors.secondaryLighter};
                    border: solid 1px ${theme.colors.secondaryLighter};
                `;
            } else if (type === `tertiary`) {
                return css`
                    background-color: ${theme.colors.secondaryDark};
                    border-bottom: solid 1px ${theme.colors.secondaryDark};
                `;
            } else {
                return css`
                    background-color: ${theme.colors.secondaryDark};
                `;
            }
        }};
    }

    &[disabled] {
        ${disabledStyles}
        pointer-events: ${(props) => (props.as === "a" ? "none" : "initial")}
    }

    &[disabled]:hover {
        cursor: not-allowed;
    }

    > i {
        transform: scale(1.4);
        ${({ iconPosition = "right" }) =>
            iconPosition === `left` &&
            css`
                order: -1;
            `}
        margin: ${({ iconPosition = "right" }) =>
            iconPosition === `left` ? `0 10px 0 0` : `0 0 0 10px`};

        &:first-child:last-child {
            margin: 0;
        }
    }
`;

const floatingButtonDimension = `40px`;
const floatingButtonPosition = `10px`;
const StyledFloatingButton = styled.button<{ customPosition: string }>`
    background: none;
    border: none;
    width: 0;
    padding: 25px 0;
    height: ${floatingButtonDimension};

    &:after,
    & > * {
        content: "";
        position: absolute;
        bottom: ${({ customPosition }) =>
            customPosition ? customPosition : floatingButtonPosition};
        right: ${({ customPosition }) =>
            customPosition ? customPosition : floatingButtonPosition};
        height: ${floatingButtonDimension};
        width: ${floatingButtonDimension};
        border-radius: 50%;
        border-color: ${(props) =>
            props.disabled
                ? props.theme.colors.backgroundDarkGrey
                : props.theme.colors.primaryDark};
        background-color: ${(props) =>
            props.disabled
                ? props.theme.colors.backgroundDarkGrey
                : props.theme.colors.primary};
        color: ${(props) => props.theme.colors.primaryButtonText};
        cursor: ${(props) => (props.disabled ? `not-allowed` : `pointer`)};
    }

    & > *:hover {
        background-color: ${(props) =>
            props.disabled
                ? props.theme.colors.backgroundDarkGrey
                : props.theme.colors.primaryDark};
    }

    & > * {
        display: block;
        z-index: ${(props) => props.theme.zIndex.sidebar};
        color: ${(props) => props.theme.colors.primaryButtonText};
        font-size: ${floatingButtonDimension};
    }

    i {
        display: flex;
    }
`;

interface FloatingButtonProps {
    icon?: string;
    onClick?: React.MouseEventHandler;
    tooltip?: string;
    tooltipPosition?: TooltipPosition;
    title?: string;
    placement?: TooltipPosition;
    disabled?: boolean;
    customPosition?: string;
    tooltipDisabled?: boolean;
}

export const FloatingButton = (props: FloatingButtonProps) => {
    const {
        icon = `plus-no-border`,
        onClick,
        tooltip,
        title,
        tooltipPosition,
        placement = `left`,
        disabled,
        customPosition,
        tooltipDisabled = false
    } = props;

    return (
        <StyledFloatingButton
            disabled={disabled}
            type="button"
            onClick={onClick}
            customPosition={customPosition}
            aria-label="Floating Button"
        >
            {tooltip ? (
                <Tooltip
                    innerHtml={tooltip}
                    distance={10}
                    position={tooltipPosition}
                    disabled={tooltipDisabled}
                >
                    <Icon name={icon} title={title} placement={placement} />
                </Tooltip>
            ) : (
                <Icon name={icon} title={title} placement={placement} />
            )}
        </StyledFloatingButton>
    );
};

export const buttonTypes = [
    `primary`,
    `secondary`,
    `tertiary`,
    `outline`,
    `secondaryOutline`
];
export const buttonSizes = [`small`, `medium`, `large`];
export const iconPositions = [`left`, `right`];
export const buttonElementTypes = [`button`, `a`];

// TODO: we should rename this as to not be confused with `next/link`
export const Link = (
    props: Omit<BtnProps, "as"> & ExtractSCProps<typeof Button>
) => <Button as="a" {...props} />;

export default Button;
